"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Map, { Marker, Popup, NavigationControl } from "react-map-gl/maplibre";
import type { MapRef } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import { MAPLIBRE_WORKER_URL } from "@/app/_module/utils/maplibreWorker";
import { PersonStanding, Footprints, DoorOpen, Car } from "lucide-react";
import type { LiveAgent } from "../../types/routes";

// OpenFreeMap (free, no API key) replaces Mapbox-hosted tiles.
const MAP_STYLE_URL = "https://tiles.openfreemap.org/styles/positron";

interface MultiAgentMapProps {
  agents: LiveAgent[];
  selectedAgentId?: string;
  onSelectAgent?: (userId: string) => void;
  height?: string;
}

function pinColorForStatus(statusLabel: string): string {
  switch (statusLabel) {
    case "Knocking":
      return "#10b981"; // green
    case "Between stops":
      return "#3b82f6"; // blue
    case "Starting":
      return "#f59e0b"; // yellow/amber
    default:
      return "#6b7280"; // gray fallback
  }
}

export default function MultiAgentMap({
  agents,
  selectedAgentId,
  onSelectAgent,
  height = "500px",
}: MultiAgentMapProps) {
  const mapRef = useRef<MapRef>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [hoveredAgentId, setHoveredAgentId] = useState<string | null>(null);
  const [visibleLabelIds, setVisibleLabelIds] = useState<Set<string>>(new Set());
  const initialFitDone = useRef(false);

  const agentsWithCoords = useMemo(
    () =>
      agents
        .map((a) => {
          const effectiveLat = a.lastLat ?? a.nextStopLat;
          const effectiveLng = a.lastLng ?? a.nextStopLng;
          const isPredicted = a.lastLat === null || a.lastLng === null;
          return { ...a, effectiveLat, effectiveLng, isPredicted };
        })
        .filter((a) => a.effectiveLat !== null && a.effectiveLng !== null),
    [agents],
  );

  // Collision-aware label visibility: project each agent to screen pixels,
  // greedily hide overlapping labels. Priority: selected > hovered > list order.
  const recomputeLabelVisibility = () => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const LABEL_W = 120; // approx width (px) of "Name • 99" chip
    const LABEL_H = 22;  // approx height (px) below the pin
    const placed: { x: number; y: number; w: number; h: number }[] = [];
    const visible = new Set<string>();

    const ordered = [...agentsWithCoords].sort((a, b) => {
      if (a.userId === selectedAgentId) return -1;
      if (b.userId === selectedAgentId) return 1;
      if (a.userId === hoveredAgentId) return -1;
      if (b.userId === hoveredAgentId) return 1;
      return 0;
    });

    for (const agent of ordered) {
      const p = map.project([
        agent.effectiveLng as number,
        agent.effectiveLat as number,
      ]);
      const box = {
        x: p.x - LABEL_W / 2,
        y: p.y + 10, // label sits below the pin
        w: LABEL_W,
        h: LABEL_H,
      };
      const overlaps = placed.some(
        (q) =>
          !(
            box.x + box.w < q.x ||
            q.x + q.w < box.x ||
            box.y + box.h < q.y ||
            q.y + q.h < box.y
          ),
      );
      const forced =
        agent.userId === selectedAgentId || agent.userId === hoveredAgentId;
      if (!overlaps || forced) {
        visible.add(agent.userId);
        placed.push(box);
      }
    }
    setVisibleLabelIds(visible);
  };

  // Auto-fit bounds on initial load
  useEffect(() => {
    if (!mapLoaded || initialFitDone.current || agentsWithCoords.length === 0)
      return;
    if (!mapRef.current) return;

    if (agentsWithCoords.length === 1) {
      const a = agentsWithCoords[0];
      mapRef.current.flyTo({
        center: [a.effectiveLng as number, a.effectiveLat as number],
        zoom: 12,
        duration: 800,
      });
    } else {
      const lngs = agentsWithCoords.map((a) => a.effectiveLng as number);
      const lats = agentsWithCoords.map((a) => a.effectiveLat as number);
      mapRef.current.fitBounds(
        [
          [Math.min(...lngs) - 0.05, Math.min(...lats) - 0.05],
          [Math.max(...lngs) + 0.05, Math.max(...lats) + 0.05],
        ],
        { padding: 80, duration: 800 },
      );
    }
    initialFitDone.current = true;
  }, [mapLoaded, agentsWithCoords]);

  // Recompute label collisions on map move/zoom + agent updates + selection/hover
  useEffect(() => {
    if (!mapLoaded) return;
    const map = mapRef.current?.getMap();
    if (!map) return;
    recomputeLabelVisibility();
    const handler = () => recomputeLabelVisibility();
    map.on("move", handler);
    map.on("zoom", handler);
    return () => {
      map.off("move", handler);
      map.off("zoom", handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLoaded, agentsWithCoords, selectedAgentId, hoveredAgentId]);

  // Fly to selected agent
  useEffect(() => {
    if (!mapLoaded || !selectedAgentId || !mapRef.current) return;
    const agent = agentsWithCoords.find((a) => a.userId === selectedAgentId);
    if (!agent) return;
    mapRef.current.flyTo({
      center: [agent.effectiveLng as number, agent.effectiveLat as number],
      zoom: 14,
      duration: 800,
    });
  }, [selectedAgentId, mapLoaded, agentsWithCoords]);

  if (agentsWithCoords.length === 0) {
    return (
      <div
        className="flex items-center justify-center bg-gray-50 rounded-xl border border-gray-200"
        style={{ height }}
      >
        <p className="text-sm text-gray-500">No active agent locations</p>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl overflow-hidden border border-gray-200 relative"
      style={{ height }}
    >
      <Map
        ref={mapRef}
        initialViewState={{
          longitude: agentsWithCoords[0]?.effectiveLng || -98.5,
          latitude: agentsWithCoords[0]?.effectiveLat || 39.8,
          zoom: 10,
        }}
        style={{ width: "100%", height: "100%" }}
        mapStyle={MAP_STYLE_URL}
        workerUrl={MAPLIBRE_WORKER_URL}
        onLoad={() => setMapLoaded(true)}
        attributionControl={false}
      >
        <NavigationControl position="top-right" showCompass={true} />

        {agentsWithCoords.map((agent) => {
          const color = pinColorForStatus(agent.statusLabel);
          const isSelected = selectedAgentId === agent.userId;
          const isHovered = hoveredAgentId === agent.userId;
          return (
            <Marker
              key={agent.userId}
              longitude={agent.effectiveLng as number}
              latitude={agent.effectiveLat as number}
              anchor="center"
              onClick={(e: any) => {
                e.originalEvent?.stopPropagation();
                onSelectAgent?.(agent.userId);
              }}
            >
              <div
                onMouseEnter={() => setHoveredAgentId(agent.userId)}
                onMouseLeave={() => setHoveredAgentId(null)}
                className="relative cursor-pointer flex flex-col items-center"
              >
                <div
                  className={`relative flex items-center justify-center rounded-full shadow-lg transition-all ${
                    agent.isPredicted
                      ? "border-[3px] border-dashed border-white/80 opacity-80"
                      : "border-[3px] border-white"
                  } ${
                    isSelected
                      ? "w-10 h-10 ring-4 ring-offset-1"
                      : "w-8 h-8"
                  } ${agent.statusLabel === "Knocking" ? "animate-pulse" : ""}`}
                  style={{
                    backgroundColor: color,
                    ...(isSelected
                      ? ({ "--tw-ring-color": `${color}66` } as any)
                      : {}),
                  }}
                >
                  {agent.statusLabel === "Knocking" ? (
                    <DoorOpen
                      className="text-white"
                      size={isSelected ? 22 : 18}
                      strokeWidth={2.5}
                    />
                  ) : agent.movementMode === "driving" ? (
                    <Car
                      className="text-white"
                      size={isSelected ? 22 : 18}
                      strokeWidth={2.5}
                    />
                  ) : agent.movementMode === "walking" ||
                    agent.statusLabel === "Between stops" ? (
                    <Footprints
                      className="text-white"
                      size={isSelected ? 22 : 18}
                      strokeWidth={2.5}
                    />
                  ) : (
                    <PersonStanding
                      className="text-white"
                      size={isSelected ? 22 : 18}
                      strokeWidth={2.5}
                    />
                  )}
                  {/* Pointer tail under the badge */}
                  <div
                    className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0"
                    style={{
                      borderLeft: "5px solid transparent",
                      borderRight: "5px solid transparent",
                      borderTop: `7px solid ${color}`,
                      filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.15))",
                    }}
                  />
                </div>
                <div
                  className={`mt-1 px-2 py-0.5 rounded-md bg-white/95 shadow-md border border-gray-200 text-[11px] font-semibold text-gray-900 whitespace-nowrap pointer-events-none transition-all ${
                    isSelected || isHovered ? "scale-105" : ""
                  } ${
                    visibleLabelIds.has(agent.userId) || isSelected || isHovered
                      ? "opacity-100"
                      : "opacity-0"
                  }`}
                >
                  {agent.name}
                  <span
                    className="ml-1 font-normal"
                    style={{ color }}
                  >
                    • {agent.doorsKnockedToday}
                  </span>
                </div>
              </div>
            </Marker>
          );
        })}

        {hoveredAgentId &&
          (() => {
            const agent = agentsWithCoords.find(
              (a) => a.userId === hoveredAgentId,
            );
            if (!agent) return null;
            return (
              <Popup
                longitude={agent.effectiveLng as number}
                latitude={agent.effectiveLat as number}
                anchor="bottom"
                closeButton={false}
                closeOnClick={false}
                offset={18}
              >
                <div className="text-xs font-medium text-gray-900">
                  {agent.name}
                </div>
                <div className="text-[10px] text-gray-500">
                  {agent.statusLabel}
                </div>
              </Popup>
            );
          })()}
      </Map>
    </div>
  );
}
